// Shared parent forward-filters (Session ADI, Section D Session 2 of the navigation-graph audit).
// A parent detail page links to each child list with an id param, so the office can drill DOWN to
// just that parent's records:
//   - the customer detail page -> sites / invoices / quotes / contracts / projects (?customerId=)
//   - the site detail page     -> its LOLER reports and its defects (?siteId=)
// Every target list reads the param, keeps only the matching rows (client-side over the already
// loaded list, or in its status default), and renders the chip so the filter is visible and
// clearable. One mechanism, reused across the lists (no per-page divergence), mirroring the Lifts
// register's "due this month" chip. The param is always the opaque id (a cuid), never a name, so no
// personal/company text ever lands in the URL.
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

// Read a single id forward-filter param and a clearer that removes it (keeping every other param).
function useIdFilter(param: string): { value: string | null; clear: () => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(param);
  const clear = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(param);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, param]);
  return { value, clear };
}

// Read the ?customerId= forward-filter (from the customer detail page's quick-links).
export function useCustomerFilter(): { customerId: string | null; clear: () => void } {
  const { value, clear } = useIdFilter("customerId");
  return { customerId: value, clear };
}

// Read the ?siteId= forward-filter (from the site detail page's LOLER / Defects links).
export function useSiteFilter(): { siteId: string | null; clear: () => void } {
  const { value, clear } = useIdFilter("siteId");
  return { siteId: value, clear };
}

// Read the ?jobId= forward-filter (from a Follow-on works pill: only the faults raised from that visit). Session SC9A.
export function useJobFilter(): { jobId: string | null; clear: () => void } {
  const { value, clear } = useIdFilter("jobId");
  return { jobId: value, clear };
}

// The dismissable "showing only this {scope}'s {noun}" chip (blue info tone, matching the Lifts
// register's due-this-month chip). Rendered by a list only while its forward-filter is active.
function FilterChip({ scope, noun, onClear }: { scope: string; noun: string; onClear: () => void }) {
  return (
    <span className="flex items-center gap-2 rounded-ele border border-ele-blue bg-ele-surface px-3 py-1.5 text-xs text-ele-blue">
      Showing only this {scope}'s {noun}
      <button onClick={onClear} className="text-ele-green hover:underline">
        Clear filter
      </button>
    </span>
  );
}

export function CustomerFilterChip({ noun, onClear }: { noun: string; onClear: () => void }) {
  return <FilterChip scope="customer" noun={noun} onClear={onClear} />;
}

export function SiteFilterChip({ noun, onClear }: { noun: string; onClear: () => void }) {
  return <FilterChip scope="site" noun={noun} onClear={onClear} />;
}

export function JobFilterNotice({ noun, onClear }: { noun: string; onClear: () => void }) {
  return <FilterChip scope="visit" noun={noun} onClear={onClear} />;
}
